import {
  CompanyRegistryEvent,
  CVROwnershipStructure,
  CVRParticipant,
} from '../types'
import { daysSince } from './format'

export type RoleActivityKind = 'role_in' | 'role_out'

export interface RoleActivityRow {
  source: 'role'
  kind: RoleActivityKind
  date: string
  bucket: keyof CVROwnershipStructure
  participant: CVRParticipant
}

export interface RegistryActivityRow {
  source: 'registry'
  event: CompanyRegistryEvent
}

export type CompanyActivityRow = RoleActivityRow | RegistryActivityRow

export type ChangeKind = 'in' | 'out'

export interface ChangeRow {
  kind: ChangeKind
  date: string
  bucket: keyof CVROwnershipStructure
  participant: CVRParticipant
}

const WINDOW_DAYS = 366

function collectChanges(
  ownership: CVROwnershipStructure,
  reference: Date,
  windowDays?: number,
  extraParticipants: Array<{
    bucket: keyof CVROwnershipStructure
    participants: CVRParticipant[]
  }> = []
): ChangeRow[] {
  const out: ChangeRow[] = []
  const buckets: Array<{
    bucket: keyof CVROwnershipStructure
    participants: CVRParticipant[]
  }> = [
    { bucket: 'beneficialOwners', participants: ownership.beneficialOwners },
    { bucket: 'ownershipRegister', participants: ownership.ownershipRegister },
    { bucket: 'management', participants: ownership.management },
    { bucket: 'boardMembers', participants: ownership.boardMembers },
    { bucket: 'auditors', participants: ownership.auditors },
    { bucket: 'other', participants: ownership.other },
    ...extraParticipants,
  ]

  const withinWindow = (iso: string | null | undefined): boolean => {
    const d = daysSince(iso, reference)
    if (d === null || d < 0) return false
    return windowDays === undefined || d <= windowDays
  }

  for (const { bucket, participants } of buckets) {
    for (const p of participants) {
      if (p.role.validFrom && withinWindow(p.role.validFrom)) {
        out.push({ kind: 'in', date: p.role.validFrom, bucket, participant: p })
      }
      if (p.role.validTo && withinWindow(p.role.validTo)) {
        out.push({ kind: 'out', date: p.role.validTo, bucket, participant: p })
      }
    }
  }

  out.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0))
  return out
}

export function collectRecentChanges(
  ownership: CVROwnershipStructure,
  reference: Date,
  founders: CVRParticipant[] = []
): ChangeRow[] {
  const extras =
    founders.length > 0 ? [{ bucket: 'other' as const, participants: founders }] : []
  return collectChanges(ownership, reference, WINDOW_DAYS, extras)
}

export function collectAllChanges(
  ownership: CVROwnershipStructure,
  reference: Date,
  founders: CVRParticipant[] = []
): ChangeRow[] {
  const extras =
    founders.length > 0 ? [{ bucket: 'other' as const, participants: founders }] : []
  return collectChanges(ownership, reference, undefined, extras)
}

function roleRowsFromChanges(
  ownership: CVROwnershipStructure,
  reference: Date,
  windowDays?: number,
  founders: CVRParticipant[] = []
): RoleActivityRow[] {
  const changes =
    windowDays === undefined
      ? collectAllChanges(ownership, reference, founders)
      : collectRecentChanges(ownership, reference, founders)

  return changes.map((row) => ({
    source: 'role' as const,
    kind: row.kind === 'in' ? 'role_in' : 'role_out',
    date: row.date,
    bucket: row.bucket,
    participant: row.participant,
  }))
}

function registryRowsInWindow(
  events: CompanyRegistryEvent[],
  reference: Date,
  windowDays?: number
): RegistryActivityRow[] {
  const withinWindow = (iso: string): boolean => {
    const d = daysSince(iso, reference)
    if (d === null || d < 0) return false
    return windowDays === undefined || d <= windowDays
  }

  return events
    .filter((event) => withinWindow(event.date))
    .map((event) => ({ source: 'registry' as const, event }))
}

function sortActivityRows(rows: CompanyActivityRow[]): CompanyActivityRow[] {
  return [...rows].sort((a, b) => {
    const dateA = a.source === 'role' ? a.date : a.event.date
    const dateB = b.source === 'role' ? b.date : b.event.date
    return dateA < dateB ? 1 : dateA > dateB ? -1 : 0
  })
}

export function collectAllCompanyActivity(
  ownership: CVROwnershipStructure,
  registryEvents: CompanyRegistryEvent[] = [],
  reference: Date = new Date(),
  founders: CVRParticipant[] = []
): CompanyActivityRow[] {
  return sortActivityRows([
    ...roleRowsFromChanges(ownership, reference, undefined, founders),
    ...registryRowsInWindow(registryEvents, reference),
  ])
}

export function collectRecentCompanyActivity(
  ownership: CVROwnershipStructure,
  registryEvents: CompanyRegistryEvent[] = [],
  reference: Date = new Date(),
  windowDays = WINDOW_DAYS,
  founders: CVRParticipant[] = []
): CompanyActivityRow[] {
  return sortActivityRows([
    ...roleRowsFromChanges(ownership, reference, windowDays, founders),
    ...registryRowsInWindow(registryEvents, reference, windowDays),
  ])
}

export function activityRowDate(row: CompanyActivityRow): string {
  return row.source === 'role' ? row.date : row.event.date
}

export function activityRowYear(row: CompanyActivityRow): string {
  return activityRowDate(row).slice(0, 4)
}

export function changeYear(date: string): string {
  return date.slice(0, 4)
}
